/**
 * pricing.js — the single source of truth for Tadlo's own plan prices.
 *
 * WHAT BELONGS HERE
 *   Tadlo's prices, and nothing else. Competitor prices (ManyChat $15,
 *   Later $16.67, CreatorFlow $39, Linktree $5/$9) stay where they are read:
 *   they are editorial claims about someone else's pricing page, they each
 *   need their own "verified on" date, and centralising them would imply a
 *   consistency we cannot enforce.
 *
 * WHERE THE NUMBERS COME FROM (2026-09-13)
 *   Prices are edited in /master/plans. The build reads them from
 *   /api/sub/plans (scripts/plan-prices.js, via build-with-version.js) and
 *   bakes them in as REACT_APP_PLAN_PRICES, so every static mention — FAQ
 *   copy, JSON-LD offers, pricing-page prose, the homepage card fallbacks —
 *   follows a master edit on the next build, with no code change. DEFAULTS
 *   below are used only when that read failed, and in tests.
 *
 *   It had to change: Pro was re-priced 399 -> 499 in master, the live price
 *   cards said 499, this file said 399, and verify-prerender (rightly) failed
 *   the deploy because the site contradicted itself.
 *
 *   verify-prerender.js reads the PRERENDERED pages and asserts every price
 *   inside a .cur span is one of the prices the build used
 *   (build/plan-prices.json). That check is how the $3-where-$5-belongs bug
 *   is prevented from returning.
 */

/** Used when the build could not read the live prices. INR is the primary
 *  market; USD is everywhere else. */
const DEFAULTS = {
  free: { id: "free", name: "Free", inr: 0, usd: 0 },
  creator: { id: "creator", name: "Creator", inr: 249, usd: 5 },
  pro: { id: "pro", name: "Pro", inr: 399, usd: 9 },
};

function livePrices() {
  // Written out in full so CRA inlines it at build time.
  const raw = process.env.REACT_APP_PLAN_PRICES;
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

const LIVE = livePrices();

function priced(id) {
  const base = DEFAULTS[id];
  const live = LIVE[id] || {};
  const num = (v, fallback) => {
    const n = Number(v);
    return v !== null && v !== undefined && v !== "" && Number.isFinite(n) && n >= 0 ? n : fallback;
  };
  return { ...base, inr: num(live.inr, base.inr), usd: num(live.usd, base.usd) };
}

/** Canonical plan prices, as this build knows them. */
export const PLANS = {
  free: priced("free"),
  creator: priced("creator"),
  pro: priced("pro"),
};

/** The cheapest PAID plan — what "starting at" and "paid entry price" mean. */
export const PAID_ENTRY = PLANS.creator;

/** Every value that may legitimately appear as a Tadlo price, by currency. */
export const ALLOWED_INR = Object.values(PLANS).map((p) => p.inr);
export const ALLOWED_USD = Object.values(PLANS).map((p) => p.usd);

export const inr = (plan) => `₹${plan.inr}`;
export const usd = (plan) => `$${plan.usd}`;
